import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { http } from "@/api/client";
import { Plus, Trash2, Edit2, X, Eye, EyeOff, Megaphone } from "lucide-react";
import { toast } from "sonner";

const PLACEMENT_LABEL = {
  top_banner: "Bandeau en haut",
  carousel: "Carrousel (rotation auto)",
  grid: "Grille « Offres en cours »",
  popup: "Popup d'accueil",
};
const PLACEMENT_ORDER = ["top_banner", "carousel", "grid", "popup"];

// Dimensions recommandées par emplacement — optimisées pour un affichage
// net sur mobile ET desktop (2× retina inclus).
const IMAGE_RECOMMENDED = {
  top_banner: {
    ratio: "Image optionnelle (le bandeau est plutôt du texte)",
    desktop: "Icône carrée : 128 × 128 px (PNG transparent recommandé)",
    mobile: "Même image, s'adapte automatiquement",
  },
  carousel: {
    ratio: "Format 4:3 (paysage)",
    desktop: "Idéal : 1600 × 1200 px · minimum 1200 × 900 px",
    mobile: "S'affiche en pleine largeur, cadrage centré",
  },
  grid: {
    ratio: "Format 16:9 (paysage large)",
    desktop: "Idéal : 1600 × 900 px · minimum 1200 × 675 px",
    mobile: "S'affiche en pleine largeur au-dessus du texte",
  },
  popup: {
    ratio: "Format 16:9 (paysage large)",
    desktop: "Idéal : 1200 × 675 px",
    mobile: "S'affiche en pleine largeur en haut de la popup",
  },
};

const EMPTY = {
  placement: "top_banner",
  title: "",
  subtitle: "",
  body_text: "",
  image_url: "",
  video_url: "",
  cta_label: "",
  cta_url: "",
  bg_color: "",
  text_color: "",
  active: true,
  starts_at: "",
  ends_at: "",
  order: 0,
};

function isoToInputDate(iso) {
  if (!iso) return "";
  return iso.slice(0, 16);
}
function inputDateToIso(s) {
  if (!s) return null;
  return new Date(s).toISOString();
}

export default function AdsAdmin() {
  const { user } = useAuth();
  const allowed = ["super_admin", "communication"].includes(user?.role);
  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = async () => {
    setLoading(true);
    try {
      const r = await http.get("/admin/ads");
      setAds(Array.isArray(r.data) ? r.data : []);
    } catch (e) {
      toast.error("Impossible de charger les publicités");
    } finally { setLoading(false); }
  };

  useEffect(() => { if (allowed) load(); }, [allowed]);

  if (!allowed) {
    return (
      <div className="text-center py-20" data-testid="ads-admin-denied">
        <h2 className="font-headings font-black text-2xl text-[#1a2332]">Accès refusé</h2>
        <p className="text-[#5b6577] mt-2">Cette section est réservée au chargé de communication et au super admin.</p>
      </div>
    );
  }

  const startNew = () => { setForm(EMPTY); setEditing({}); };
  const startEdit = (ad) => {
    setForm({
      ...EMPTY,
      ...ad,
      starts_at: isoToInputDate(ad.starts_at),
      ends_at: isoToInputDate(ad.ends_at),
    });
    setEditing(ad);
  };
  const cancel = () => { setEditing(null); setForm(EMPTY); };

  const save = async (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      starts_at: inputDateToIso(form.starts_at),
      ends_at: inputDateToIso(form.ends_at),
      order: Number(form.order) || 0,
    };
    try {
      if (editing && editing.id) {
        await http.patch(`/admin/ads/${editing.id}`, payload);
      } else {
        await http.post("/admin/ads", payload);
      }
      toast.success(editing && editing.id ? "Publicité mise à jour" : "Publicité créée");
      cancel();
      load();
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Erreur inconnue";
      toast.error(`Erreur : ${String(msg).slice(0, 200)}`);
    }
  };

  const toggleActive = async (ad) => {
    try {
      await http.patch(`/admin/ads/${ad.id}`, { active: !ad.active });
      load();
    } catch { toast.error("Impossible de mettre à jour"); }
  };

  const del = async (ad) => {
    if (!window.confirm(`Supprimer définitivement « ${ad.title || ad.placement} » ?`)) return;
    try {
      await http.delete(`/admin/ads/${ad.id}`);
      toast.success("Publicité supprimée");
      load();
    } catch { toast.error("Suppression impossible"); }
  };

  // Group ads by placement for clarity
  const grouped = PLACEMENT_ORDER.reduce((acc, p) => {
    acc[p] = ads.filter((a) => a.placement === p);
    return acc;
  }, {});

  return (
    <div className="space-y-6" data-testid="ads-admin">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="font-headings font-black text-3xl text-[#1a2332] flex items-center gap-3">
            <Megaphone className="w-7 h-7 text-[#7c3aed]" /> Publicités du site
          </h1>
          <p className="text-[#5b6577] mt-1">Gérez les promos, bandeaux, popups et offres en cours affichés sur la home.</p>
        </div>
        <button
          onClick={startNew}
          data-testid="ads-admin-new"
          className="inline-flex items-center gap-2 bg-[#00a86b] hover:bg-[#0a4d33] text-white font-headings font-bold rounded-xl px-4 py-2.5 text-sm shadow-md"
        >
          <Plus className="w-4 h-4" /> Nouvelle publicité
        </button>
      </div>

      {/* Editor */}
      {editing !== null && (
        <form onSubmit={save} className="bg-white border border-black/5 rounded-2xl p-6 shadow-md space-y-4" data-testid="ads-admin-form">
          <div className="flex items-center justify-between">
            <h2 className="font-headings font-black text-xl text-[#1a2332]">
              {editing.id ? "Modifier la publicité" : "Nouvelle publicité"}
            </h2>
            <button type="button" onClick={cancel} className="text-[#5b6577] hover:text-[#1a2332]"><X className="w-5 h-5" /></button>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <label className="text-sm">
              <span className="font-headings font-semibold text-[#1a2332]">Emplacement</span>
              <select
                value={form.placement}
                onChange={(e) => setForm({ ...form, placement: e.target.value })}
                data-testid="ads-form-placement"
                className="mt-1 w-full bg-[#f8faff] rounded-xl px-3 py-2 border border-black/5"
              >
                {PLACEMENT_ORDER.map((p) => <option key={p} value={p}>{PLACEMENT_LABEL[p]}</option>)}
              </select>
            </label>

            <label className="text-sm">
              <span className="font-headings font-semibold text-[#1a2332]">Ordre d&apos;affichage</span>
              <input
                type="number" value={form.order}
                onChange={(e) => setForm({ ...form, order: e.target.value })}
                data-testid="ads-form-order"
                className="mt-1 w-full bg-[#f8faff] rounded-xl px-3 py-2 border border-black/5"
              />
            </label>

            <label className="text-sm sm:col-span-2">
              <span className="font-headings font-semibold text-[#1a2332]">Titre</span>
              <input
                type="text" maxLength={200} value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                data-testid="ads-form-title"
                placeholder="Ex : 🎉 Promo -20% sur le forfait Permis B"
                className="mt-1 w-full bg-[#f8faff] rounded-xl px-3 py-2 border border-black/5"
              />
            </label>

            <label className="text-sm sm:col-span-2">
              <span className="font-headings font-semibold text-[#1a2332]">Sous-titre</span>
              <input
                type="text" maxLength={300} value={form.subtitle}
                onChange={(e) => setForm({ ...form, subtitle: e.target.value })}
                data-testid="ads-form-subtitle"
                placeholder="Ex : Jusqu'au 31 mars · offre limitée"
                className="mt-1 w-full bg-[#f8faff] rounded-xl px-3 py-2 border border-black/5"
              />
            </label>

            <label className="text-sm sm:col-span-2">
              <span className="font-headings font-semibold text-[#1a2332]">Texte / description (carrousel, grille, popup)</span>
              <textarea
                rows={3} maxLength={1000} value={form.body_text}
                onChange={(e) => setForm({ ...form, body_text: e.target.value })}
                data-testid="ads-form-body"
                className="mt-1 w-full bg-[#f8faff] rounded-xl px-3 py-2 border border-black/5"
              />
            </label>

            <label className="text-sm">
              <span className="font-headings font-semibold text-[#1a2332]">URL image (https://…)</span>
              <input
                type="url" value={form.image_url}
                onChange={(e) => setForm({ ...form, image_url: e.target.value })}
                data-testid="ads-form-image"
                placeholder={`https://… — ${IMAGE_RECOMMENDED[form.placement]?.desktop || ""}`}
                className="mt-1 w-full bg-[#f8faff] rounded-xl px-3 py-2 border border-black/5"
              />
              <div className="mt-1.5 text-[11px] text-[#5b6577] bg-[#f0fdf4] rounded-lg px-2.5 py-1.5 border border-[#00a86b]/15 leading-relaxed" data-testid="ads-form-image-hint">
                <div><span className="font-headings font-bold text-[#00a86b]">📐 {IMAGE_RECOMMENDED[form.placement]?.ratio}</span></div>
                <div>💻 Desktop : {IMAGE_RECOMMENDED[form.placement]?.desktop}</div>
                <div>📱 Mobile : {IMAGE_RECOMMENDED[form.placement]?.mobile}</div>
              </div>
            </label>

            <label className="text-sm">
              <span className="font-headings font-semibold text-[#1a2332]">URL vidéo (optionnelle)</span>
              <input
                type="url" value={form.video_url}
                onChange={(e) => setForm({ ...form, video_url: e.target.value })}
                data-testid="ads-form-video"
                placeholder="https://…"
                className="mt-1 w-full bg-[#f8faff] rounded-xl px-3 py-2 border border-black/5"
              />
            </label>

            <label className="text-sm">
              <span className="font-headings font-semibold text-[#1a2332]">Texte du bouton</span>
              <input
                type="text" maxLength={80} value={form.cta_label}
                onChange={(e) => setForm({ ...form, cta_label: e.target.value })}
                data-testid="ads-form-cta-label"
                placeholder="Ex : En profiter"
                className="mt-1 w-full bg-[#f8faff] rounded-xl px-3 py-2 border border-black/5"
              />
            </label>

            <label className="text-sm">
              <span className="font-headings font-semibold text-[#1a2332]">URL du bouton</span>
              <input
                type="text" value={form.cta_url}
                onChange={(e) => setForm({ ...form, cta_url: e.target.value })}
                data-testid="ads-form-cta-url"
                placeholder="#tarifs ou https://…"
                className="mt-1 w-full bg-[#f8faff] rounded-xl px-3 py-2 border border-black/5"
              />
            </label>

            <label className="text-sm">
              <span className="font-headings font-semibold text-[#1a2332]">Couleur de fond (hex)</span>
              <input
                type="text" value={form.bg_color}
                onChange={(e) => setForm({ ...form, bg_color: e.target.value })}
                data-testid="ads-form-bg"
                placeholder="#1a2332"
                className="mt-1 w-full bg-[#f8faff] rounded-xl px-3 py-2 border border-black/5"
              />
            </label>

            <label className="text-sm">
              <span className="font-headings font-semibold text-[#1a2332]">Couleur du texte (hex)</span>
              <input
                type="text" value={form.text_color}
                onChange={(e) => setForm({ ...form, text_color: e.target.value })}
                data-testid="ads-form-text"
                placeholder="#FFFFFF"
                className="mt-1 w-full bg-[#f8faff] rounded-xl px-3 py-2 border border-black/5"
              />
            </label>

            <label className="text-sm">
              <span className="font-headings font-semibold text-[#1a2332]">Affichage à partir de (optionnel)</span>
              <input
                type="datetime-local" value={form.starts_at}
                onChange={(e) => setForm({ ...form, starts_at: e.target.value })}
                data-testid="ads-form-starts-at"
                className="mt-1 w-full bg-[#f8faff] rounded-xl px-3 py-2 border border-black/5"
              />
            </label>

            <label className="text-sm">
              <span className="font-headings font-semibold text-[#1a2332]">Affichage jusqu&apos;au (optionnel)</span>
              <input
                type="datetime-local" value={form.ends_at}
                onChange={(e) => setForm({ ...form, ends_at: e.target.value })}
                data-testid="ads-form-ends-at"
                className="mt-1 w-full bg-[#f8faff] rounded-xl px-3 py-2 border border-black/5"
              />
            </label>

            <label className="text-sm flex items-center gap-2 mt-2">
              <input
                type="checkbox" checked={form.active}
                onChange={(e) => setForm({ ...form, active: e.target.checked })}
                data-testid="ads-form-active"
                className="w-4 h-4"
              />
              <span className="font-headings font-semibold text-[#1a2332]">Actif (visible sur le site)</span>
            </label>
          </div>

          <div className="flex gap-3 pt-2">
            <button
              type="submit"
              data-testid="ads-form-save"
              className="bg-[#00a86b] hover:bg-[#0a4d33] text-white font-headings font-bold rounded-xl px-5 py-2.5 text-sm shadow-md"
            >
              {editing.id ? "Enregistrer les modifications" : "Créer la publicité"}
            </button>
            <button
              type="button" onClick={cancel}
              className="text-[#5b6577] font-headings font-semibold text-sm px-4"
              data-testid="ads-form-cancel"
            >
              Annuler
            </button>
          </div>
        </form>
      )}

      {/* List grouped by placement */}
      {loading ? (
        <div className="text-center py-20 text-[#5b6577]">Chargement…</div>
      ) : (
        <div className="space-y-6">
          {PLACEMENT_ORDER.map((p) => (
            <section key={p} data-testid={`ads-group-${p}`}>
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-headings font-bold text-lg text-[#1a2332]">{PLACEMENT_LABEL[p]}</h3>
                <span className="text-xs text-[#5b6577] bg-white rounded-full px-3 py-1 border border-black/5">{grouped[p].length} pub{grouped[p].length > 1 ? "s" : ""}</span>
              </div>
              {grouped[p].length === 0 ? (
                <p className="text-sm text-[#5b6577] bg-white/50 rounded-xl px-4 py-3 border border-dashed border-black/10">Aucune publicité dans cet emplacement.</p>
              ) : (
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {grouped[p].map((ad) => (
                    <article
                      key={ad.id}
                      data-testid={`ad-row-${ad.id}`}
                      className={`rounded-2xl border p-4 bg-white shadow-sm space-y-2 ${ad.active ? "border-[#00a86b]/30" : "border-black/10 opacity-60"}`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className={`inline-flex items-center gap-1 text-[10px] font-headings font-bold uppercase tracking-wide rounded-full px-2 py-0.5 ${ad.active ? "bg-[#00a86b]/10 text-[#00a86b]" : "bg-black/5 text-[#5b6577]"}`}>
                          {ad.active ? "Actif" : "Inactif"}
                        </span>
                        <div className="flex gap-1">
                          <button onClick={() => toggleActive(ad)} aria-label="Toggle actif"
                            data-testid={`ad-toggle-${ad.id}`}
                            className="p-1 text-[#5b6577] hover:text-[#1a2332]" title="Activer/désactiver">
                            {ad.active ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                          </button>
                          <button onClick={() => startEdit(ad)} aria-label="Éditer"
                            data-testid={`ad-edit-${ad.id}`}
                            className="p-1 text-[#5b6577] hover:text-[#00a86b]">
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button onClick={() => del(ad)} aria-label="Supprimer"
                            data-testid={`ad-delete-${ad.id}`}
                            className="p-1 text-[#5b6577] hover:text-red-600">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                      {ad.image_url && (
                        <div className="aspect-[16/9] rounded-lg overflow-hidden bg-[#f8faff]">
                          <img src={ad.image_url} alt={ad.title || ""} className="w-full h-full object-cover" />
                        </div>
                      )}
                      <h4 className="font-headings font-bold text-[#1a2332] text-sm leading-snug">{ad.title || <em className="text-[#5b6577]">Sans titre</em>}</h4>
                      {ad.subtitle && <p className="text-xs text-[#5b6577] line-clamp-2">{ad.subtitle}</p>}
                      <div className="text-[10px] text-[#5b6577] flex flex-wrap gap-x-3 gap-y-0.5 pt-1 border-t border-black/5">
                        {ad.starts_at && <span>Du {new Date(ad.starts_at).toLocaleDateString("fr-FR")}</span>}
                        {ad.ends_at && <span>Au {new Date(ad.ends_at).toLocaleDateString("fr-FR")}</span>}
                        {ad.cta_label && <span>CTA : {ad.cta_label}</span>}
                      </div>
                    </article>
                  ))}
                </div>
              )}
            </section>
          ))}
        </div>
      )}
    </div>
  );
}
